/* StaDesks motion layer — scroll reveals, parallax, count-ups, header shrink, scroll progress.
 *
 * Progressive enhancement only: content is visible without JS (the `.js-motion` class that hides
 * reveal targets is set by this file), and everything is skipped under prefers-reduced-motion.
 */
(() => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const root = document.documentElement;
  const qsa = (s, r = document) => [...r.querySelectorAll(s)];

  // ---- header shrink + scroll progress (cheap, rAF-throttled) ---------------------------------
  const header = document.querySelector("[data-header]") || document.querySelector(".header");
  const bar = document.createElement("div");
  bar.className = "scroll-progress";
  document.body.appendChild(bar);

  const parallax = reduce ? [] : qsa("[data-parallax]").map((el) => ({
    el: el.tagName === "IMG" ? el : el.querySelector("img") || el,
    host: el,
    k: Number(el.dataset.parallax) || 0.1,
  }));

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      header?.classList.toggle("is-scrolled", y > 24);
      const vh = window.innerHeight;
      parallax.forEach(({ el, host, k }) => {
        const r = host.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        // clamp the drift and over-scale by the same amount, so the image never uncovers its frame
        const lim = r.height * k * 0.5;
        const shift = Math.max(-lim, Math.min(lim, (r.top + r.height / 2 - vh / 2) * -k));
        el.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${(1 + k * 1.1).toFixed(3)})`;
      });
      ticking = false;
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  onScroll();

  if (reduce) return;
  root.classList.add("js-motion");

  // ---- auto-tag the existing theme sections so they reveal too --------------------------------
  // Reveals are opt-in (data-reveal in the markup) — no blanket fade-up on every card.

  // stagger siblings that share a parent
  const groups = new Map();
  qsa("[data-reveal]").forEach((el) => {
    const p = el.parentElement;
    if (!groups.has(p)) groups.set(p, []);
    groups.get(p).push(el);
  });
  groups.forEach((list) => list.forEach((el, i) => el.style.setProperty("--d", `${Math.min(i, 6) * 70}ms`)));

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add("is-in");
        io.unobserve(e.target);
        qsa("[data-count]", e.target).forEach(countUp);
        if (e.target.matches("[data-count]")) countUp(e.target);
      });
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
  );
  qsa("[data-reveal]").forEach((el) => io.observe(el));

  // anything already in view on load shows immediately (no flash of hidden above-the-fold)
  requestAnimationFrame(() =>
    qsa("[data-reveal]").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) el.classList.add("is-in");
    })
  );

  // ---- count-up: animates the leading number, keeps any unit ("12 cm" → 0…12 cm) --------------
  function countUp(el) {
    if (el.dataset.counted) return;
    el.dataset.counted = "1";
    const raw = el.dataset.count || el.textContent;
    const m = String(raw).match(/^([^\d]*)([\d.,]+)(.*)$/);
    if (!m) return;
    const target = Number(m[2].replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(target)) return;
    const decimals = (m[2].split(",")[1] || "").length;
    const t0 = performance.now();
    const dur = 1100;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const v = target * (1 - Math.pow(1 - p, 3));
      el.textContent = m[1] + v.toLocaleString("nl-NL", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + m[3];
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // ---- hero intro: staggered lines --------------------------------------------------------------
  qsa("[data-hero] [data-stagger]").forEach((el, i) => el.style.setProperty("--d", `${120 + i * 110}ms`));
  requestAnimationFrame(() => document.querySelector("[data-hero]")?.classList.add("is-ready"));

  // ---- magnetic primary buttons (desktop pointer only) ------------------------------------------
  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    qsa(".btn-glow").forEach((btn) => {
      btn.addEventListener("pointermove", (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.setProperty("--mx", `${e.clientX - r.left}px`);
        btn.style.setProperty("--my", `${e.clientY - r.top}px`);
      });
    });
  }
})();
